import { BurstMode, Headset, PhotoCamera, Videocam } from '@mui/icons-material';
import {
  MEDIA_TYPES_LABEL,
  MediasWrapper,
  PreviewSize,
} from '@prismamedia/one-components';
import { MetadataNameEnumType } from '../../__generated__/queries-photo';
import { RecipeStatus } from '../../__generated__/queries-recipe';
import {
  ArticleFormat,
  ArticleStatus,
  BrandKey,
} from '../../__generated__/queries-web';
import { getMetadata } from '../../apollo/utils/getMetadata';
import { assert } from '../../utils/assert';
import { getImageFromArticleBody } from '../../utils/getImageFromArticleBody';
import {
  getImageFromMedias,
  getMediasSite,
  getThumbnailUrlFromPhoto,
  getVideoThumbnail,
} from '../../utils/media';
import { statusToLabel } from '../../utils/statuses';
import { embedImage } from '../../utils/upload';
import { standardizeArticleUrl } from '../../utils/url';
import { baseRecipesURLs } from '../InternalLinkModal/useInternalLinksGetter';
import { FotoSearch } from './FotoSearch';
import { ProviderIcon } from './FotoSearch/ProvidersPhoto/Icons';
import { PhotoCollapsesTypes } from './FotoSearch/types';
import { embedMediaFromArticle } from './Plugins';
import { PodcastSearch } from './PodcastSearch';
import { MEDIA_TYPES } from './constants';

const scheduledStatusLabel = (status: ArticleStatus | RecipeStatus) => {
  if (status === ArticleStatus.Scheduled || status === RecipeStatus.Scheduled)
    return 'Programmé le';
};

export const getAllowedMedias = (
  allowedMedia: MEDIA_TYPES[],
  { subjectId }: { subjectId?: string },
) => {
  const allowedMedias: MediasWrapper[] = [];

  allowedMedia.forEach((mediaType) => {
    if (mediaType === MEDIA_TYPES.ARTICLE_VIDEO) {
      allowedMedias.push({
        Icon: Videocam,
        label: 'Video',
        type: MEDIA_TYPES_LABEL.VIDEOS,
      });
    } else if (mediaType === MEDIA_TYPES.SLIDESHOW) {
      allowedMedias.push({
        Icon: BurstMode,
        label: 'Diapo',
        type: MEDIA_TYPES_LABEL.SLIDESHOWS,
      });
    } else if (mediaType === MEDIA_TYPES.PODCAST) {
      allowedMedias.push({
        Component: PodcastSearch,
        Icon: Headset,
        label: 'Podcast',
        type: MEDIA_TYPES_LABEL.PODCASTS,
      });
    } else if (mediaType === MEDIA_TYPES.FOTOWEB) {
      allowedMedias.push({
        Component: FotoSearch,
        Icon: PhotoCamera,
        label: 'Photo',
        props: { topicId: subjectId },
        type: MEDIA_TYPES_LABEL.IMAGES,
      });
    }
  });

  return allowedMedias;
};

export const getItemToRender = (
  brandKey: BrandKey,
  item: any,
  pluginType?: string,
) => {
  switch (pluginType) {
    case MEDIA_TYPES_LABEL.ARTICLES:
      return {
        chip: item.brandKey,
        id: item.id,
        image:
          getImageFromMedias(item.medias)?.mediaForDisplay ||
          item?.imageSrc?.mediaForDisplay,
        publicUrl: standardizeArticleUrl(item?.contextualizedUrl) || item?.url,
        publishDate: item.publishedAt,
        text: item.articleCreatorData?.name,
        title: item.title,
        status: scheduledStatusLabel(item.status),
      };
    case MEDIA_TYPES_LABEL.RECIPES:
      return {
        chip: item.brandKey,
        id: item.id,
        image:
          getImageFromMedias(item.medias)?.mediaForDisplay ||
          item?.imageSrc?.mediaForDisplay,
        title: item.title,
        publishDate: item.publishedAt,
        publicUrl:
          item?.url ||
          baseRecipesURLs[brandKey] +
            assert(item, ({ recipeUrls }) => recipeUrls[0].url?.path),
        status: scheduledStatusLabel(item.status),
      };
    case MEDIA_TYPES_LABEL.VIDEOS:
      return {
        chip: item.brandKey,
        id: item.id,
        image:
          getVideoThumbnail(item.medias) || item?.imageSrc?.mediaForDisplay,
        publicUrl: standardizeArticleUrl(item?.contextualizedUrl) || item?.url,
        publishDate: item.publishedAt,
        site: getMediasSite(item.medias)?.[0],
        status: `${
          (statusToLabel(ArticleFormat.Video) as any)[item.status]
        } le`,
        text: item.articleCreatorData?.name,
        title: item.title,
      };
    case MEDIA_TYPES_LABEL.PODCASTS:
      return {
        feed: item.feed,
        id: item.id,
        image: item.image,
        isLoading: false,
        text: item.text,
        title: item.title,
      };
    case MEDIA_TYPES_LABEL.SLIDESHOWS:
      return {
        chip: item.brandKey,
        id: item.id,
        image: getImageFromArticleBody(item.body, false),
        publicUrl: standardizeArticleUrl(item?.contextualizedUrl),
        publishDate: item.publishedAt,
        text: item.articleCreatorData?.name,
        title: item.title,
        status: scheduledStatusLabel(item.status),
        slideShowArticleCount: item.slideShowArticleCount,
      };
    case MEDIA_TYPES_LABEL.IMAGES:
      return {
        ...(item.from === PhotoCollapsesTypes.PROVIDERS && {
          chip: <ProviderIcon provider={item.provider} />,
        }),
        id: item.id || item.idSubject,
        image:
          item.from === PhotoCollapsesTypes.PROVIDERS
            ? item.medium || item.large || item.small
            : getThumbnailUrlFromPhoto(item, PreviewSize.xs),
        text:
          item.from === PhotoCollapsesTypes.ONE_PHOTOS
            ? getMetadata(MetadataNameEnumType.Source, item)
            : item.from === PhotoCollapsesTypes.ONE_STORY
            ? item.source
            : item.name,
        title:
          item.from === PhotoCollapsesTypes.ONE_PHOTOS
            ? getMetadata(MetadataNameEnumType.Credit, item) ||
              getMetadata(MetadataNameEnumType.title, item) ||
              getMetadata(MetadataNameEnumType.description, item)
            : item.credit || item.title || item.description,
      };
    default:
      return {
        id: 'no-plugin-type',
      };
  }
};

const getRecipeMediaUrl = (medias: string | null) => {
  const imageSrc = getImageFromMedias(medias);

  if (imageSrc?.media) {
    return imageSrc.media.iframely.meta.canonical;
  }
};

export const getItemToIframely = async (item: any, pluginType: string) => {
  switch (pluginType) {
    case MEDIA_TYPES_LABEL.ARTICLES:
      return await embedMediaFromArticle(item, ArticleFormat.Rich);
    case MEDIA_TYPES_LABEL.RECIPES:
      return await embedImage(getRecipeMediaUrl(item.medias));
    case MEDIA_TYPES_LABEL.VIDEOS:
      return await embedMediaFromArticle(item, ArticleFormat.Video);
    case MEDIA_TYPES_LABEL.SLIDESHOWS:
      return await embedMediaFromArticle(item, ArticleFormat.Slideshow);
  }
};
